import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FileDown, Pencil, Trash, PackageMinus } from "lucide-react";
import { toast } from "react-toastify";

import ListScaffold from "@/components/desktop/list/table/ListScaffold";
import CustomDropdown from "@/components/desktop/list/dropdown/CustomDropdown";
import CustomBadge from "@/components/desktop/badge/CustomBadge";
import BasicConfirm from "@/components/Confirm/BasicConfirm";
import CustomFetching from "@/components/desktop/fetching/CustomFetching";
import CustomNoPermissionView from "@/components/desktop/permission/no-permission/CustomNoPermissionView";

import useFilterStore from "@/stores/useFilterStore";
import useListController from "@/hooks/list/useListController";
import { useScreenPermissions } from "@/hooks/general/useScreenPermissions";
import { useEstatesList, useEstatesLookups, useDeleteEstate } from "@/hooks/maintainers/estates/useEstates";
import { useEstatesExport } from "@/hooks/maintainers/estates/useEstatesExport";
import { validRoles } from "@/services/Utils";
import { SCREEN_ID_ESTATE } from "@/constants/screenIds";

import { base_table } from "./estates.constants";

import EstatesForm from "./Form/EstatesForm";
import EstateOutModal from "./EstatesForm/EstateOutModal";

const COLUMN_WIDTHS = {
	index: "5%",
	name: "20%",
	estateParent: "17%",
	categories: "32%",
	implemented: "14%",
	action: "12%",
};

const Estates = () => {
	const navigate = useNavigate();
	const { filter } = useFilterStore();
	const { estate_id, estate_parent_id, search } = filter || {};

	const userRoles = JSON.parse(localStorage.getItem("roles"));
	const isAdmin = validRoles(userRoles, ["administrador"]);

	const { canShow, canEdit, isLoading: isLoadingPermissions } = useScreenPermissions(SCREEN_ID_ESTATE);
	const canLoadScreenData = !isLoadingPermissions && canShow;

	const { tableState, reactTableSort, handlePageChange, handlePageSizeChange, handleSortByChange } = useListController({
		baseFilter: [],
		baseTable: base_table,
	});

	const listFilters = useMemo(
		() => ({
			estate_id,
			estate_parent_id,
			search,
			...tableState,
		}),
		[estate_id, estate_parent_id, search, tableState],
	);

	const { data: lookups, isLoading: isLoadingLookups } = useEstatesLookups({
		enabled: canLoadScreenData,
	});

	const { data: listResponse, isLoading: isLoadingList } = useEstatesList(listFilters, {
		enabled: canLoadScreenData && Boolean(lookups),
	});

	const { mutate: deleteEstate, isLoading: isLoadingDelete } = useDeleteEstate();
	const { handleExport } = useEstatesExport(listFilters);

	const estatesParents = useMemo(() => lookups?.data?.estates_parents ?? [], [lookups]);
	const categories = useMemo(() => lookups?.data?.categories ?? [], [lookups]);

	const [showDelete, setShowDelete] = useState(false);
	const [showOut, setShowOut] = useState(false);
	const [selectedId, setSelectedId] = useState(null);

	const handleDelete = () => {
		setShowDelete(false);
		deleteEstate(selectedId, {
			onSuccess: () => toast.success("Bodega eliminada correctamente"),
			onError: (error) => toast.error(error?.response?.data?.error || "Error al eliminar la bodega"),
		});
	};

	const handleOpenDelete = useCallback((id) => {
		setSelectedId(id);
		setShowDelete(true);
	}, []);

	const handleOpenOut = useCallback((id) => {
		setSelectedId(id);
		setShowOut(true);
	}, []);

	const columns = useMemo(() => {
		return [
			{
				Header: "#",
				accessor: "",
				disableSortBy: true,
				width: COLUMN_WIDTHS.index,
				Cell: ({ row }) => <span>{(tableState.page - 1) * tableState.per_page + row.index + 1}</span>,
			},
			{
				Header: "Nombre",
				accessor: "name",
				width: COLUMN_WIDTHS.name,
			},
			{
				Header: "Campo",
				accessor: "estate_parent_name",
				width: COLUMN_WIDTHS.estateParent,
				disableSortBy: true,
			},
			{
				Header: "Tipo de bodega",
				accessor: "categories_id",
				width: COLUMN_WIDTHS.categories,
				disableSortBy: true,
				Cell: ({ value }) => {
					if (!value) return null;
					const ids = String(value)
						.split(",")
						.map((id) => id.trim());
					return (
						<div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
							{ids.map((id) => {
								const cat = categories.find((c) => String(c.value) === id);
								return (
									<CustomBadge key={id} variant="neutral">
										{cat ? cat.label : id}
									</CustomBadge>
								);
							})}
						</div>
					);
				},
			},
			{
				Header: "Implementado",
				accessor: "implemented",
				width: COLUMN_WIDTHS.implemented,
				disableSortBy: true,
				Cell: ({ value }) => (
					<CustomBadge variant={value ? "success" : "neutral"} dot>
						{value ? "Implementado" : "No implementado"}
					</CustomBadge>
				),
			},
			{
				Header: "Acción",
				accessor: "slug",
				disableSortBy: true,
				width: COLUMN_WIDTHS.action,
				Cell: ({ value, row }) => {
					const items = [];

					if (canEdit) {
						items.push({
							label: "Editar",
							action: () => navigate("/configuracion/campos/editar/" + value),
							icon: Pencil,
						});
					}

					if (canEdit) {
						items.push({
							label: "Eliminar",
							action: () => handleOpenDelete(row.original.id),
							icon: Trash,
							danger: true,
						});
					}

					if (isAdmin) {
						items.unshift({
							label: "Sacar productos",
							action: () => handleOpenOut(row.original.id),
							icon: PackageMinus,
						});
					}

					if (!items.length) return null;

					return <CustomDropdown title="Acciones" items={items} align="right" />;
				},
			},
		];
	}, [tableState.page, tableState.per_page, categories, isAdmin, canEdit, navigate, handleOpenDelete, handleOpenOut]);

	if (isLoadingPermissions) {
		return <CustomFetching center message="Validando permisos..." />;
	}

	if (!canShow) {
		return <CustomNoPermissionView />;
	}

	return (
		<ListScaffold
			titleBreadcrumbs={[
				{ label: "Dashboard", onClick: () => navigate("/") },
				{ label: "Configuración", onClick: () => navigate("/configuracion") },
				{ label: "Bodegas", onClick: () => navigate("/configuracion/campos") },
			]}
			subtitle="Gestiona las bodegas y su configuración"
			showCreate={canEdit}
			createLabel="Nueva Bodega"
			onCreate={() => navigate("/configuracion/campos/crear")}
			actionButtons={[
				{
					key: "excel",
					label: "Excel",
					onClick: handleExport,
					icon: FileDown,
					variant: "secondary",
				},
			]}
			isLoading={isLoadingList || isLoadingLookups || isLoadingDelete}
			data={listResponse?.data || []}
			totalItems={listResponse?.totalRecords || 0}
			totalPages={listResponse?.totalPages || 0}
			toItem={listResponse?.data?.length || 0}
			fromItem={listResponse?.page === 1 ? 1 : (listResponse?.page - 1) * tableState.per_page + 1}
			pageIndex={tableState.page}
			columns={columns}
			onPageChange={handlePageChange}
			pageSize={tableState.per_page}
			onPageSizeChange={handlePageSizeChange}
			sortBy={reactTableSort}
			onSortByChange={handleSortByChange}
			pageSizeOptions={[20, 50, 100]}
			initialPageSize={tableState.per_page}
			initialSortBy={reactTableSort}
		>
			<EstatesForm estatesParents={estatesParents} categories={categories} filters={listFilters} />

			<EstateOutModal
				open={showOut}
				onClose={() => {
					setShowOut(false);
					setSelectedId(null);
				}}
				estateId={selectedId}
			/>

			<BasicConfirm
				showConfirm={showDelete}
				setShowConfirm={setShowDelete}
				handleAction={handleDelete}
				subMessage="Al eliminar el registro de Bodega se eliminarán todos los datos asociados. ¿Está seguro que desea eliminar?"
				showValidation={true}
				textValidation="eliminar bodega y todos sus datos"
			/>
		</ListScaffold>
	);
};

export default Estates;
